import {ref, computed} from "vue";
import isEqual from "lodash.isequal";
import uniq from "lodash.uniq";

export function useProductOptions(product, defaultVariantId) {
  const selectedOptions = ref([]);

  const setOptionsByVariantId = variantId => {
    const localOptions = [];

    // Sold-out options are disabled in the UI, so opening on one would leave
    // the shopper stuck on "Find Store". Honour the requested variant only
    // while it is in stock, otherwise fall through to the first one that is;
    // when the whole product is sold out we keep the requested variant so the
    // store-locator CTA still has something to render.
    const requestedVariant = variantId
      ? product.variants.find(variant => variant.id === variantId)
      : undefined;

    const firstAvailableVariant =
      (requestedVariant?.available && requestedVariant) ||
      product.variants.find(variant => variant.available) ||
      requestedVariant ||
      product.variants[0];

    if (!firstAvailableVariant) return;

    product.options.map(option => {
      localOptions.push({
        type: option.name.toLowerCase(),
        value: firstAvailableVariant[`option${option.position}`],
        position: option.position,
      });
    });

    selectedOptions.value = localOptions;
  };

  const firstOption = computed(() =>
    selectedOptions.value.find(option => option.position === 1),
  );

  const eligibleVariants = computed(() =>
    firstOption.value
      ? product.variants.filter(
          variant => variant.option1 === firstOption.value.value,
        )
      : [],
  );

  const eligibleOptions = computed(() =>
    uniq(
      eligibleVariants.value
        .flatMap(variant => [variant.option1, variant.option2, variant.option3])
        .filter(Boolean),
    ),
  );

  const variantToPurchase = computed(() => {
    const flatOptions = selectedOptions.value
      .map(option => String(option.value))
      .sort();
    const found = product.variants.find(variant => {
      const options = [...variant.options];
      return isEqual(flatOptions, options.sort());
    });

    return found;
  });

  const productOptions = computed(() => {
    if (!product) return;
    return product.options;
  });

  const findVariantsByOptions = obj => {
    if (!isAvailableOption(obj)) return;

    const index = selectedOptions.value.findIndex(opt => opt.type === obj.type);

    if (index === -1) {
      selectedOptions.value.push(obj);
    } else {
      selectedOptions.value.splice(index, 1, obj);
    }
  };

  const isActiveOption = option => {
    if (!selectedOptions.value) return false;
    return selectedOptions.value.find(opt => isEqual(opt, option));
  };

  const isVisibleOption = option => {
    /**
     * Option 1 is the "anchor" option that always needs to be skipped because it always needs to be available
     */
    if (option.position === 1) {
      return true;
    } else {
      return eligibleOptions.value.includes(String(option.value));
    }
  };

  /**
   * Is there a variant in stock for this option value?
   *
   * Option 1 is the anchor, so it is judged across the whole product; the
   * later options are judged within the combination already selected, the
   * same narrowing `isVisibleOption` uses.
   */
  const isAvailableOption = option => {
    const key = `option${option.position}`;
    const constraints =
      option.position === 1
        ? []
        : selectedOptions.value.filter(
            opt => opt.position !== option.position,
          );

    return product.variants.some(variant => {
      if (!variant.available) return false;
      if (String(variant[key]) !== String(option.value)) return false;
      return constraints.every(
        opt => String(variant[`option${opt.position}`]) === String(opt.value),
      );
    });
  };

  if (eligibleVariants.value.length === 1) {
    const option = product.options[0];
    selectedOptions.value = [
      {
        type: option.name.toLowerCase(),
        position: option.position,
        value: option.value[0],
      },
    ];
  }

  setOptionsByVariantId(defaultVariantId);

  return {
    selectedOptions,
    firstOption,
    eligibleVariants,
    eligibleOptions,
    variantToPurchase,
    productOptions,
    findVariantsByOptions,
    setOptionsByVariantId,
    isVisibleOption,
    isAvailableOption,
    isActiveOption,
  };
}
